// Take a card, without earning one.
//
// Cards are a *set of things to choose between*, so they are tiles rather than
// a column of full-width rows — the same argument the scenario screen's board
// picker is built on.
//
// **Nothing on a tile is clipped** (owed since U7; `DevMenu.css` clamped
// `card.text` to three lines and put the whole rule on a native `title`, which
// was the last clip in the interface and one of the last titles). It takes
// `HandFan.tsx`'s line: the face says `card.short` — the terse account that exists
// so a face never has to clip — and the hover card says the whole of
// `card.text`.

import type { DevDispatch } from "../DevMenu";
import { HoverCard, styled } from "../kit";
import { useGame } from "../useGame";
import { useBesidePanel, Note, Fold } from "./parts";

const Grid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: theme.kit.space.xs
}));

/**
 * One card's face. `Tile`'s skin at a card's shape — a fixed 66px column with a
 * caption under a glyph is the hotbar's geometry and holds neither a name nor a
 * sentence.
 */
const Face = styled("button")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: theme.kit.space.sm,
  textAlign: "left",
  background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  border: `2px solid ${theme.kit.color.line}`,
  borderRadius: theme.kit.radius.sm,
  color: theme.kit.color.ink,
  cursor: "pointer",
  "&:hover": { borderColor: theme.kit.color.lineHi },
  "&:focus-visible": { outline: `2px solid ${theme.kit.color.brand}`, outlineOffset: 2 }
}));

const Named = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 12.5,
  lineHeight: 1.2,
  color: theme.kit.color.ink
}));

/** The terse account. No clamp, because `short` is what a face can hold. */
const Short = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 11,
  lineHeight: 1.35,
  color: theme.kit.color.inkDim
}));

export function GrantCards({
  dispatch,
  inRun
}: {
  dispatch: DevDispatch;
  /** Cards live in a run's deck, so there has to be a run to put one in. */
  inRun: boolean;
}) {
  const panel = useBesidePanel();
  // Every card the rules know, in their order: the catalog is `CARD_LIST`'s reader.
  const cards = useGame().catalog.cards();
  return (
    <Fold id="cards" title="Take a card">
      {!inRun ? (
        // Said out loud rather than by disabling the button silently — a
        // control that does nothing and does not explain is the trap this
        // project keeps paying for.
        <Note>
          Cards live in a run&apos;s deck, and this game has no run. Start one from the menu —
          single player, or Local for a bare board.
        </Note>
      ) : (
        <>
          <Grid>
            {cards.map((card) => (
              <HoverCard
                key={card.id}
                placement="right"
                beside={panel}
                title={card.name}
                card={card.text}
              >
                <Face
                  type="button"
                  data-testid={`dev-grant-${card.id}`}
                  onClick={() => dispatch({ type: "grant-card", card: card.id })}
                >
                  <Named>{card.name}</Named>
                  <Short>{card.short}</Short>
                </Face>
              </HoverCard>
            ))}
          </Grid>
          <Note>
            Goes straight to your hand. It is an ordinary command, so it is in the log and the
            session still replays.
          </Note>
        </>
      )}
    </Fold>
  );
}
